import { memo, useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import type { LeadCloudFileType } from '@econtractor/types';
import FileItem from './FileItem';
import { isPreviewImage } from './helper/filesUtils';
import type { FilesViewMode } from './model/filesView';
import 'glightbox/dist/css/glightbox.min.css';

interface FilesListProps {
    files: LeadCloudFileType[];
    view: FilesViewMode;
    galleryId: string;
    imageColProps?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
    };
    canDelete?: boolean;
    deletingKey?: string | null;
    renamingKey?: string | null;
    onDelete?: (file: LeadCloudFileType) => void;
    onRename?: (file: LeadCloudFileType) => void;
    noShowPreviewImages?: boolean;
    canDrag?: boolean;
    movingKey?: string | null;
}

const FilesList = memo(
    ({
        files,
        view,
        galleryId,
        imageColProps,
        canDelete,
        deletingKey,
        renamingKey,
        onDelete,
        onRename,
        noShowPreviewImages,
        canDrag,
        movingKey,
    }: FilesListProps) => {
        const fileItems = useMemo(() => files.filter((file) => !file.isFolder), [files]);
        const previewFiles = useMemo(
            () => (noShowPreviewImages ? [] : fileItems.filter((file) => isPreviewImage(file.fileName))),
            [fileItems, noShowPreviewImages],
        );
        const colProps = view === 'list' ? { xs: 12 } : imageColProps || { xs: 6, md: 4 };

        useEffect(() => {
            if (!previewFiles.length) return undefined;
            const GLightbox = require('glightbox');
            const lightbox = GLightbox({
                selector: '.glightbox',
            });

            return () => lightbox.destroy();
        }, [previewFiles]);

        if (!fileItems.length) return null;

        return (
            <Row>
                {fileItems.map((file) => (
                    <Col className="p-1" key={file.storageKey} {...colProps}>
                        <FileItem
                            file={file}
                            galleryId={galleryId}
                            canDelete={canDelete}
                            busy={
                                deletingKey === file.storageKey ||
                                renamingKey === file.storageKey ||
                                movingKey === file.storageKey
                            }
                            onDelete={() => onDelete?.(file)}
                            onRename={() => onRename?.(file)}
                            showPreview={!noShowPreviewImages && isPreviewImage(file.fileName)}
                            canDrag={canDrag}
                        />
                    </Col>
                ))}
            </Row>
        );
    },
    (prevProps, nextProps) =>
        prevProps.files === nextProps.files &&
        prevProps.view === nextProps.view &&
        prevProps.deletingKey === nextProps.deletingKey &&
        prevProps.renamingKey === nextProps.renamingKey &&
        prevProps.canDelete === nextProps.canDelete &&
        prevProps.onDelete === nextProps.onDelete &&
        prevProps.onRename === nextProps.onRename &&
        prevProps.galleryId === nextProps.galleryId &&
        prevProps.imageColProps === nextProps.imageColProps &&
        prevProps.noShowPreviewImages === nextProps.noShowPreviewImages &&
        prevProps.canDrag === nextProps.canDrag &&
        prevProps.movingKey === nextProps.movingKey,
);

export default FilesList;
